import { z } from 'zod';
import { INDICATOR_CONFIDENCE, INDICATOR_TYPES, PRIVACY_CATEGORIES, SECURITY_CATEGORIES, THREAT_CATEGORIES } from '../threat/types.js';
import { EXTENSION_ID_RE } from '../extensions/types.js';

/**
 * Protection bundle (Phases 3–6): a SEPARATE signed artifact from the policy bundle, fetched by
 * extension versions that understand it (GET /ext/v1/protection). The policy bundle schema is
 * strict and unchanged, so extensions already deployed keep accepting policy updates.
 * Signed with the same keyring and envelope shape as the policy bundle (bigger size cap).
 */
const domain = z.string().min(1).max(253).regex(/^[a-z0-9.-]+$/);
const extId = z.string().regex(EXTENSION_ID_RE);

export const PROTECTION_SCHEMA_VERSION = 1;

export const protectionBundleSchema = z
  .object({
    schemaVersion: z.literal(PROTECTION_SCHEMA_VERSION),
    orgId: z.string().min(1).max(64),
    version: z.number().int().min(0),
    createdAt: z.string().datetime(),
    web: z
      .object({
        enabled: z.boolean(),
        /** block: navigation stopped (block page); warn: interstitial with continue; monitor: event only. */
        mode: z.enum(['block', 'warn', 'monitor']),
        categories: z.array(z.enum(SECURITY_CATEGORIES)).max(SECURITY_CATEGORIES.length),
        /** Deny list compiled from feeds + org lists for the enabled categories. `c` = index into `categories`. */
        domains: z.array(z.object({ d: domain, c: z.number().int().min(0).max(31) }).strict()).max(200_000),
        /** Organisation allow list: always wins over feeds. */
        allowDomains: z.array(domain).max(10_000),
      })
      .strict(),
    downloads: z
      .object({
        enabled: z.boolean(),
        /** Known-malicious source URL/domain or known-bad payload hash (server lookup) → block. */
        blockKnownMalicious: z.boolean(),
        /** Executable from a source outside the popular-sites list → warn. */
        warnUnknownExecutables: z.boolean(),
        executableExtensions: z.array(z.string().min(1).max(16).regex(/^[a-z0-9]+$/)).max(100),
        /** Popular-sites allow set used by warnUnknownExecutables (registrable domains). */
        reputableDomains: z.array(domain).max(100_000),
        /** Ask the server (POST /ext/v1/reputation) about download URLs not decided locally. */
        serverLookup: z.boolean(),
      })
      .strict(),
    privacy: z
      .object({
        enabled: z.boolean(),
        categories: z.array(z.enum(PRIVACY_CATEGORIES)).max(PRIVACY_CATEGORIES.length),
        /** Third-party requests to these domains are blocked (never main-frame navigations). */
        trackerDomains: z.array(domain).max(50_000),
        /** Query parameters removed from navigations (e.g. utm_source, fbclid, gclid). */
        stripParams: z.array(z.string().min(1).max(64).regex(/^[A-Za-z0-9_.-]+$/)).max(200),
        allowDomains: z.array(domain).max(10_000),
      })
      .strict(),
    extensions: z
      .object({
        /** Blocked extensions Ward disables immediately where the browser allows (Edge policy stays authoritative). */
        disableIds: z.array(extId).max(1000),
      })
      .strict(),
  })
  .strict();
export type ProtectionBundle = z.infer<typeof protectionBundleSchema>;

export const signedProtectionSchema = z
  .object({
    payload: z.string().max(16 * 1024 * 1024),
    signature: z.string().max(200),
    keyId: z.string().max(64),
    alg: z.literal('ES256'),
  })
  .strict();
export type SignedProtection = z.infer<typeof signedProtectionSchema>;

/** POST /ext/v1/reputation (device → server). */
export const reputationRequestSchema = z
  .object({
    urls: z.array(z.string().url().max(2048)).max(20).default([]),
    sha256: z.array(z.string().regex(/^[a-f0-9]{64}$/)).max(20).default([]),
  })
  .strict();
export type ReputationRequest = z.infer<typeof reputationRequestSchema>;

/** Result of a reputation lookup for one queried value. */
const reputationMatchSchema = z.object({
  type: z.enum(INDICATOR_TYPES),
  value: z.string().max(4096),
  /** "block" = on a deny list / feed; "allow" = on an organisation allow list (overrides feeds). */
  disposition: z.enum(['block', 'allow']),
  category: z.enum(THREAT_CATEGORIES).nullable(),
  source: z.string().max(200),
  confidence: z.enum(INDICATOR_CONFIDENCE),
  reference: z.string().max(2000).nullable(),
});
export type ReputationMatch = z.infer<typeof reputationMatchSchema>;

/** POST /ext/v1/reputation response (server → device). */
export const reputationResponseSchema = z.object({ matches: z.array(reputationMatchSchema).max(200) });
